import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import supabase from "../supabaseClient";
import "./Company.css";

export default function CompanyPage() {
  const [company, setCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sliderImages, setSliderImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { id } = useParams();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    async function fetchData() {
      try {
        setLoading(true);

        // Fetch company
        const { data: companyData, error: companyError } = await supabase
          .from("companies")
          .select("id, name, logo_url, short_description, description")
          .eq("id", id)
          .single();

        if (signal.aborted) return;
        if (companyError) {
          console.error("Company fetch error:", companyError);
          return;
        }
        setCompany(companyData);

        // Fetch projects
        const { data: projectsData, error: projectsError } = await supabase
          .from("projects")
          .select(
            "id, name, description, location, budget, status, start_date, end_date, projectUrl"
          )
          .eq("company_id", companyData?.id);

        if (signal.aborted) return;
        if (projectsError) {
          console.error("Projects fetch error:", projectsError);
          return;
        }
        setProjects(projectsData || []);
        setLoading(false);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Fetch error:", error);
        }
        setLoading(false);
      }
    }

    const fetchSliderImages = async () => {
      const { data, error } = await supabase
        .from("sliders")
        .select("image_url")
        //.eq("company_id", id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Slider image fetch error:", error);
      } else {
        setSliderImages(data.map((item) => item.image_url));
      }
    };

    fetchData();
    fetchSliderImages();

    return () => {
      controller.abort();
    };
  }, [id]);

  // Auto-advance slider
  useEffect(() => {
    if (sliderImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [sliderImages.length]);

  const getCompanyGradient = () => {
    const color = company?.color || "#3b82f6";
    return `linear-gradient(135deg, ${color}, ${color}dd)`;
  };

  const formatBudget = (budget) => {
    if (budget >= 1000000) {
      return `$${(budget / 1000000).toFixed(1)}M`;
    } else if (budget >= 1000) {
      return `$${(budget / 1000).toFixed(1)}K`;
    }
    return `$${budget}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "#10b981";
      case "in-progress":
        return "#f59e0b";
      case "planning":
        return "#8b5cf6";
      default:
        return "#64748b";
    }
  };

  if (loading) {
    return (
      <div className="company-loading">
        <div className="loading-spinner"></div>
        <h3>Loading company details...</h3>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="company-error">
        <div className="error-content">
          <h2>Company Not Found</h2>
          <p>The requested company could not be found.</p>
          <Link to="/" className="btn-home">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="company-page">
      {/* Navigation */}
      <nav className="company-navbar">
        <div className="nav-container">
          <Link to="/" className="logo">
            <h2>← Back to Home</h2>
          </Link>
          <div className="nav-links">
            <a href="#projects">Projects</a>
            <a href="/contact">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="company-hero">
        <div
          className="company-hero-background"
          style={{ background: getCompanyGradient() }}
        >
          <div className="angled-overlay"></div>
        </div>

        <div className="company-hero-content">
          <div className="hero-text-section">
            <div className="company-logo-container">
              {company.logo_url && (
                <img
                  src={company.logo_url}
                  alt={`${company.name} logo`}
                  className="company-logo"
                />
              )}
            </div>
            <h1 className="company-title">{company.name}</h1>
           

            <div className="hero-buttons">
              <p>{company.short_description}</p>
            </div>
          </div>

          <div className="hero-slider-section">
            {sliderImages.length > 0 && (
              <div className="company-slider">
                <div className="slider-container">
                  {sliderImages.map((image, index) => (
                    <div
                      key={index}
                      className={`slide ${
                        index === currentSlide ? "active" : ""
                      }`}
                      style={{ backgroundImage: `url(${image})` }}
                    />
                  ))}
                </div>

                {sliderImages.length > 1 && (
                  <>
                    <div className="slider-dots">
                      {sliderImages.map((_, index) => (
                        <button
                          key={index}
                          className={`dot ${
                            index === currentSlide ? "active" : ""
                          }`}
                          onClick={() => setCurrentSlide(index)}
                        />
                      ))}
                    </div>

                    <button
                      className="slider-nav prev"
                      onClick={() =>
                        setCurrentSlide(
                          (prev) =>
                            (prev - 1 + sliderImages.length) %
                            sliderImages.length
                        )
                      }
                    >
                      ‹
                    </button>
                    <button
                      className="slider-nav next"
                      onClick={() =>
                        setCurrentSlide(
                          (prev) => (prev + 1) % sliderImages.length
                        )
                      }
                    >
                      ›
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <section>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-header">
                <h2 className="section-title">Company Overview</h2>
                <p className="section-subtitle">{company.description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Projects Section */}
      <section id="projects" className="projects-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Projects</h2>
            <p className="section-subtitle">
              Discover our latest work and achievements
            </p>
          </div>

          {projects.length > 0 ? (
            <div className="projects-grid">
              {projects.map((project) => (
                <div key={project.id} className="project-card">
                  <div className="project-header">
                    {project.projectUrl && (
                      <div className="project-image">
                        <img src={project.projectUrl} alt={project.name} />
                      </div>
                    )}
                    <div
                      className="project-status"
                      style={{
                        backgroundColor: getStatusColor(project.status),
                      }}
                    >
                      {project.status || "Active"}
                    </div>
                  </div>

                  <div className="project-content">
                    <h3 className="project-title">{project.name}</h3>
                    <p className="project-description">{project.description}</p>

                    <div className="project-details">
                      {project.location && (
                        <div className="detail-item">
                          <span className="detail-icon">📍</span>
                          <span>{project.location}</span>
                        </div>
                      )}
                      {project.budget && (
                        <div className="detail-item">
                          <span className="detail-icon">💰</span>
                          <span>{formatBudget(project.budget)}</span>
                        </div>
                      )}
                      {project.start_date && (
                        <div className="detail-item">
                          <span className="detail-icon">📅</span>
                          <span>
                            {new Date(project.start_date).getFullYear()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="project-footer">
                    <button className="project-btn">View Details</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-projects">
              <div className="no-projects-content">
                <h3>No Projects Yet</h3>
                <p>This company hasn't added any projects to showcase.</p>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
